import * as React from "react";
import { Button, Dropdown, Form } from "react-bootstrap";
import TableView from "../../../components/tableView";
import DeleteUserGuideModal from "./DeleteUserGuideModal";
import UserGuideModal from "./UserGuideModal";
import {
  IAddUserGuidePayload,
  IEditUserGuidePayload,
  IUserGuide,
  UserGuideModalMode,
} from "../userGuideTypes";
import { filterUserGuides, openUserGuideFile } from "../utils";
import { PlusIconWhite } from "../../../components/ui/icons";

interface ISuperAdminUserGuideProps {
  userGuides: IUserGuide[];
  isSubmitting: boolean;
  isDeleting: boolean;
  onAddGuide: (payload: IAddUserGuidePayload) => Promise<void>;
  onEditGuide: (payload: IEditUserGuidePayload) => Promise<void>;
  onDeleteGuide: (guideId: string) => Promise<void>;
}

const SuperAdminUserGuide = ({
  userGuides,
  isSubmitting,
  isDeleting,
  onAddGuide,
  onEditGuide,
  onDeleteGuide,
}: ISuperAdminUserGuideProps) => {
  const [searchText, setSearchText] = React.useState("");
  const [isGuideModalOpen, setIsGuideModalOpen] = React.useState(false);
  const [guideModalMode, setGuideModalMode] =
    React.useState<UserGuideModalMode>("add");
  const [selectedGuide, setSelectedGuide] = React.useState<IUserGuide | null>(
    null,
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  const filteredGuides = React.useMemo(
    () => filterUserGuides(userGuides, searchText),
    [userGuides, searchText],
  );

  const handleAddClick = (): void => {
    setSelectedGuide(null);
    setGuideModalMode("add");
    setIsGuideModalOpen(true);
  };

  const handleEditClick = (guide: IUserGuide): void => {
    setSelectedGuide(guide);
    setGuideModalMode("edit");
    setIsGuideModalOpen(true);
  };

  const handleDeleteClick = (guide: IUserGuide): void => {
    setSelectedGuide(guide);
    setIsDeleteModalOpen(true);
  };

  const handleGuideSubmit = async (
    payload: IAddUserGuidePayload | IEditUserGuidePayload,
  ): Promise<void> => {
    try {
      if (guideModalMode === "add") {
        await onAddGuide(payload as IAddUserGuidePayload);
      } else {
        await onEditGuide(payload as IEditUserGuidePayload);
      }

      setIsGuideModalOpen(false);
      setSelectedGuide(null);
    } catch {}
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!selectedGuide?.id) {
      return;
    }

    try {
      await onDeleteGuide(selectedGuide.id);
      setIsDeleteModalOpen(false);
      setSelectedGuide(null);
    } catch {}
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Title",
        enableColumnFilter: false,
        cell: ({ row }: any) => {
          const title = row.original.title || "-";

          return (
            <div className="user-guide-table-title" title={title}>
              {title}
            </div>
          );
        },
      },
      {
        accessorKey: "description",
        header: "Description",
        enableColumnFilter: false,
        cell: ({ row }: any) => {
          const description = row.original.description || "-";

          return (
            <div
              className="user-guide-table-description"
              title={row.original.description || ""}
            >
              {description}
            </div>
          );
        },
      },
      {
        accessorKey: "fileName",
        header: "File",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: any) => {
          const guide = row.original as IUserGuide;

          return (
            <button
              className="user-guide-file-button"
              disabled={!guide.fileUrl}
              onClick={() => openUserGuideFile(guide.fileUrl)}
            >
              View
            </button>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: any) => {
          const guide = row.original as IUserGuide;

          return (
            <Dropdown align="end" className="user-guide-action-dropdown">
              <Dropdown.Toggle className="user-guide-action-toggle">
                ⋮
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleEditClick(guide)}>
                  Edit
                </Dropdown.Item>
                <Dropdown.Item
                  className="delete-dropdown-item"
                  onClick={() => handleDeleteClick(guide)}
                >
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      <div className="user-guide-heading user-guide-heading-with-actions">
        <h1>User Guide</h1>

        <div className="user-guide-toolbar">
          <Form.Control
            type="text"
            value={searchText}
            placeholder="Search by title or description"
            className="user-guide-search"
            onChange={(event) => setSearchText(event.target.value)}
          />

          <Button className="user-guide-add-button" onClick={handleAddClick}>
            <PlusIconWhite />
            Add Guide
          </Button>
        </div>
      </div>

      <div className="user-guide-body">
        <div className="super-admin-user-guide">
          <div className="user-guide-table-wrapper">
            {filteredGuides.length ? (
              <TableView
                data={filteredGuides}
                columns={columns}
                noDataMessage="No help guides found."
              />
            ) : (
              <div className="user-guide-empty-table">
                <div className="user-guide-empty-table-header">
                  <div>Title</div>
                  <div>Description</div>
                  <div>File</div>
                  <div>Actions</div>
                </div>

                <div className="user-guide-empty-table-body">
                  {searchText
                    ? "No matching help guides found."
                    : "No help guides found."}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <UserGuideModal
        show={isGuideModalOpen}
        mode={guideModalMode}
        selectedGuide={selectedGuide}
        userGuides={userGuides}
        isSubmitting={isSubmitting}
        onClose={() => setIsGuideModalOpen(false)}
        onSubmit={handleGuideSubmit}
      />

      <DeleteUserGuideModal
        show={isDeleteModalOpen}
        selectedGuide={selectedGuide}
        isDeleting={isDeleting}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default SuperAdminUserGuide;
